/**
 * Connectivity / asset-reachability probes for the assessment.
 *
 * Answers "could this candidate actually load the audio?" at the moment they
 * took the test, so a blocked network is visible in the record instead of
 * needing to be reverse-engineered afterwards.
 *
 * Usage (merge into the existing device log):
 *
 *   import { getDeviceLog } from "./helper";
 *   import { getConnectivity } from "./connectivityCheck";
 *
 *   const log = { ...getDeviceLog(step), ...(await getConnectivity(step, audioUrl)) };
 *
 * Everything is time-boxed and failure-tolerant — it will never block the test.
 */

const STATIC_REPO = process.env.REACT_APP_STATIC_REPO || "";
const SERVER_URL = process.env.REACT_APP_SERVER_URL || "";

// a small, always-present asset used as the canary
const CANARY = "short_dialogue/sl_no_01_customer.mp3";

const MEDIA_ERR = {
  1: "ABORTED — fetch cancelled",
  2: "NETWORK — request blocked or connection dropped",
  3: "DECODE — file reached us but could not be decoded",
  4: "SRC_NOT_SUPPORTED — blocked, 404, or wrong content type",
};

/**
 * Probe a media URL the same way the real <audio> element would.
 * Works cross-origin without CORS, because we listen to element events
 * rather than reading the response.
 */
const probeAudio = (url, timeoutMs = 6000) =>
  new Promise((resolve) => {
    const t0 = performance.now();
    const el = new Audio();
    let settled = false;

    const finish = (ok, detail) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        el.src = "";
        el.load();
      } catch (e) {
        /* noop */
      }
      resolve({ ok, ms: Math.round(performance.now() - t0), detail });
    };

    const timer = setTimeout(() => finish(false, `timeout after ${timeoutMs}ms`), timeoutMs);

    el.addEventListener("loadedmetadata", () => finish(true, "loadedmetadata"));
    el.addEventListener("canplaythrough", () => finish(true, "canplaythrough"));
    el.addEventListener("error", () => {
      const e = el.error;
      finish(false, e ? MEDIA_ERR[e.code] || `MediaError code ${e.code}` : "unknown error");
    });

    el.preload = "metadata";
    el.crossOrigin = "anonymous";
    el.src = url;
    try {
      el.load();
    } catch (e) {
      finish(false, `load threw: ${e.message}`);
    }
  });

/** Probe an HTTP endpoint. no-cors so a cross-origin block still surfaces. */
const probeHttp = async (url, timeoutMs = 5000) => {
  const t0 = performance.now();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    await fetch(url, { method: "GET", mode: "no-cors", cache: "no-store", signal: ctrl.signal });
    clearTimeout(timer);
    return { ok: true, ms: Math.round(performance.now() - t0), detail: "reachable" };
  } catch (e) {
    clearTimeout(timer);
    return {
      ok: false,
      ms: Math.round(performance.now() - t0),
      detail: e.name === "AbortError" ? `timeout after ${timeoutMs}ms` : `${e.name}: ${e.message}`,
    };
  }
};

/** Microphone permission without triggering a prompt. */
const micPermission = async () => {
  try {
    if (!navigator.permissions?.query) return "unsupported";
    const s = await navigator.permissions.query({ name: "microphone" });
    return s.state; // granted | denied | prompt
  } catch (e) {
    return "unsupported";
  }
};

/**
 * @param {string} step        current assessment step
 * @param {string} [audioPath] relative path of the audio this step needs
 *                             (e.g. "fill_blanks_audio/sl_no_07_morning_routine.m4a")
 */
export const getConnectivity = async (step, audioPath) => {
  const targets = [
    ["staticHost", probeHttp(STATIC_REPO)],
    ["canaryAudio", probeAudio(STATIC_REPO + CANARY)],
    ["apiHost", probeHttp(SERVER_URL)],
    ["micPermission", micPermission()],
  ];

  if (audioPath) {
    targets.push(["stepAudio", probeAudio(STATIC_REPO + audioPath)]);
  }

  const settled = await Promise.allSettled(targets.map(([, p]) => p));
  const out = {};
  targets.forEach(([name], i) => {
    out[name] = settled[i].status === "fulfilled" ? settled[i].value : { ok: false, detail: "probe crashed" };
  });

  const audioBlocked = out.canaryAudio && out.canaryAudio.ok === false;

  return {
    connectivity: {
      ...out,
      checkedAt: new Date().toISOString(),
      staticRepo: STATIC_REPO,
      audioPath: audioPath || null,
      // single flag for dashboards / filtering
      audioReachable: !audioBlocked,
      verdict: audioBlocked
        ? "AUDIO UNREACHABLE — likely network/proxy block on the static host"
        : "ok",
    },
  };
};

export default getConnectivity;
